/*eslint angular/component-limit: 'off'*/
(function () {
    'use strict';

    /**
     * Use `formatTime` for showing standard hours and minute
     * You probably want to add a title to the date element, use `formatLongDate` for that
     *
     * https://docs.angularjs.org/api/ng/filter/date for date format patterns
     */

    // Cached translated date names - initialized in .run() block and updated on language change
    const dateNames = {
        // Full day names: Sunday, Monday, etc.
        fullDayNames: [],
        // Short day names: Sun, Mon, etc.
        shortDayNames: [],
        // Full month names: January, February, etc.
        fullMonthNames: [],
        // Short month names (first 3 chars): Jan, Feb, etc.
        shortMonthNames: []
    };

    /**
     * Updates the cached date names with current translations
     * Called on app start and when language changes
     *
     * @param {Object} $translate - Translation service
     */
    function updateDateNames($translate) {
        dateNames.fullDayNames = [
            $translate.instant('Sunday'),
            $translate.instant('Monday'),
            $translate.instant('Tuesday'),
            $translate.instant('Wednesday'),
            $translate.instant('Thursday'),
            $translate.instant('Friday'),
            $translate.instant('Saturday')
        ];

        dateNames.shortDayNames = [
            $translate.instant('Sun'),
            $translate.instant('Mon'),
            $translate.instant('Tue'),
            $translate.instant('Wed'),
            $translate.instant('Thu'),
            $translate.instant('Fri'),
            $translate.instant('Sat')
        ];

        dateNames.fullMonthNames = [
            $translate.instant('January'),
            $translate.instant('February'),
            $translate.instant('March'),
            $translate.instant('April'),
            $translate.instant('May'),
            $translate.instant('June'),
            $translate.instant('July'),
            $translate.instant('August'),
            $translate.instant('September'),
            $translate.instant('October'),
            $translate.instant('November'),
            $translate.instant('December')
        ];

        dateNames.shortMonthNames = dateNames.fullMonthNames.map(function (name) {
            return name.slice(0, 3);
        });
    }

    dateFiltersRun.$inject = ['$translate', '$rootScope'];

    function dateFiltersRun($translate, $rootScope) {
        // Initialize date names on app start
        updateDateNames($translate);

        // Update date names when language changes
        const deregisterTranslateChange = $rootScope.$on('$translateChangeSuccess', function () {
            updateDateNames($translate);
        });

        // Clean up listener on $rootScope destroy (app shutdown)
        $rootScope.$on('$destroy', deregisterTranslateChange);
    }

    angular
        .module('salesflare')
        .run(dateFiltersRun)
        .filter('formatDate', ['dateFilter', 'model', formatDate])
        .filter('formatQuickDate', ['dateFilter', 'model', formatQuickDate])
        .filter('formatShortDate', ['dateFilter', 'model', '$translate', formatShortDate])
        .filter('formatSimpleDate', ['$translate', formatSimpleDate])
        .filter('formatMonthYear', [formatMonthYear])
        .filter('formatFutureDate', ['dateFilter', 'model', formatFutureDate])
        .filter('formatLongDate', ['dateFilter', 'model', '$translate', formatLongDate])
        .filter('formatFullDate', ['$translate', formatFullDate])
        .filter('formatAngularLongDate', ['dateFilter', '$translate', formatAngularLongDate])
        .filter('formatTime', ['dateFilter', 'model', formatTime])
        .filter('timeAgo', ['$translate', timeAgo])
        .filter('timeFromNow', ['$translate', timeFromNow])
        .filter('secondsToDays', ['$translate', secondsToDays])
        .filter('secondsToDaysAndHours', ['$translate', secondsToDaysAndHours])
        .filter('formatToTheSecondRoundedUp', ['dateFilter', '$translate', formatToTheSecondRoundedUp]);

    function formatDate(dateFilter, model) {

        return function (input) {

            if (!input) {
                return '';
            }

            const date = new Date(input);
            const datemillis = new Date(date).setHours(0, 0, 0, 0);
            const today = new Date();
            const todaymillis = today.setHours(0, 0, 0, 0);

            if (datemillis === todaymillis) {
                return dateFilter(date, pickTimeFormat(model));
            }
            else if (datemillis > todaymillis) {
                return formatTranslatedDate(date, 'd MMM');
            }

            return formatTranslatedDate(date, 'd MMM');
        };
    }

    /**
     * Helper function to format dates with translated month names
     *
     * @param {Date} date - The date to format
     * @param {String} format - Format pattern ('d MMM' or 'd MMM y')
     * @returns {String} Formatted date with translated month names
     */
    function formatTranslatedDate(date, format) {

        const day = date.getDate();
        const monthAbbr = dateNames.shortMonthNames[date.getMonth()];
        const year = date.getFullYear();

        if (format === 'd MMM y') {
            return day + ' ' + monthAbbr + ' ' + year;
        }

        return day + ' ' + monthAbbr;
    }

    function formatQuickDate(dateFilter, model) {

        return function (input) {

            if (!input) {
                return null;
            }

            const date = new Date(input);
            const weekday = dateNames.shortDayNames[date.getDay()];
            const time = dateFilter(date, pickTimeFormat(model));

            return weekday + ' ' + time;
        };
    }

    function formatShortDate(dateFilter, model, $translate) {

        return function (input, includePrefix) {

            let prefix = ` '${$translate.instant('on')}' `;
            if (!input) {
                return null;
            }

            const date = new Date(input);

            const oneDay = (24 * 60 * 60 * 1000); // Hours*minutes*seconds*milliseconds
            const inputTime = date.getTime();
            const currentTime = Date.now();
            const diffDays = Math.round(Math.abs((inputTime - currentTime) / (oneDay)));
            let dateFormat;

            if (diffDays === 0) {
                prefix = ` '${$translate.instant('at')}' `;
                dateFormat = pickTimeFormat(model);
            }
            else if (diffDays < 7) {
                return ' ' + $translate.instant('on') + ' ' + dateNames.shortDayNames[date.getDay()];
            }
            else if (date.getFullYear() === new Date().getFullYear()) {
                dateFormat = 'd MMM';
            }
            else {
                dateFormat = 'd MMM y';
            }

            if (includePrefix) {
                dateFormat = prefix + dateFormat;
            }

            return dateFilter(date, dateFormat);
        };
    }

    /**
     * Returns `05 Dec` or `Today` when `showToday === true` and `input` is today
     *
     * @param {Object} $translate
     * @returns {Function}
     */
    function formatSimpleDate($translate) {

        return function (input, showToday) {

            if (!input) {
                return null;
            }

            const date = new Date(input);

            let result = formatTranslatedDate(date, 'd MMM');

            if (showToday) {
                const oneDay = 24 * 60 * 60 * 1000; // Hours*minutes*seconds*milliseconds
                const inputTime = date.getTime();
                const currentTime = Date.now();
                const diffDays = Math.round(Math.abs((inputTime - currentTime) / (oneDay)));

                if (diffDays === 0) {
                    result = $translate.instant('Today');
                }
            }

            return result;
        };
    }

    /**
     * Returns `January 2024` format with translated month name
     *
     * @returns {Function}
     */
    function formatMonthYear() {

        return function (input) {

            if (!input) {
                return null;
            }

            const date = new Date(input);
            const month = dateNames.fullMonthNames[date.getMonth()];
            const year = date.getFullYear();

            return month + ' ' + year;
        };
    }

    function formatFutureDate(dateFilter, model) {

        return function (input) {

            if (!input) {
                return null;
            }

            //Input = input + " UTC";
            //date = new Date(input.replace(/-/g, "/"));
            const date = new Date(input);
            const datemillis = new Date(date).setHours(0, 0, 0, 0);

            const today = new Date();
            const todaymillis = today.setHours(0, 0, 0, 0);

            const lastweek = new Date();
            lastweek.setDate(today.getDate() + 7);
            const lastweekmillis = lastweek.setHours(0, 0, 0, 0);

            if (datemillis === todaymillis) {
                return dateFilter(date, pickTimeFormat(model));
            }
            else if (datemillis < lastweekmillis) {
                return dateNames.shortDayNames[date.getDay()];
            }

            return formatTranslatedDate(date, 'd MMM');
        };
    }

    /**
     * Returns `Sun 5 December 2017 at 04:20`
     *
     * @param {Function} dateFilter
     * @param {Object} model
     * @param {Object} $translate
     * @returns {Function}
     */
    function formatLongDate(dateFilter, model, $translate) {

        return function (input) {

            if (!input) {
                return null;
            }

            //Input = input + " UTC";
            //date = new Date(input.replace(/-/g, "/"));
            const date = new Date(input);

            const dayName = dateNames.shortDayNames[date.getDay()];
            const day = date.getDate();
            const monthName = dateNames.fullMonthNames[date.getMonth()];
            const year = date.getFullYear();

            const formattedDate = dayName + ' ' + day + ' ' + monthName + ' ' + year;
            const formattedTime = dateFilter(date, pickTimeFormat(model));

            return $translate.instant('{{date}} at {{time}}', {
                date: formattedDate,
                time: formattedTime
            });
        };
    }

    /**
     * Returns `Wednesday, January 5, 2025`
     * Full day name, full month name, day, year - with translated names
     *
     * @param {Object} $translate
     * @returns {Function}
     */
    function formatFullDate($translate) {

        return function (input) {

            if (!input) {
                return null;
            }

            const date = new Date(input);

            const dayName = dateNames.fullDayNames[date.getDay()];
            const day = date.getDate();
            const monthName = dateNames.fullMonthNames[date.getMonth()];
            const year = date.getFullYear();

            // Format: "Wednesday, January 5, 2025"
            return $translate.instant('{{dayName}}, {{monthName}} {{day}}, {{year}}', { dayName, monthName, day, year });
        };
    }

    /**
     * Returns `Dec 5, 2017, 04:20:03 PM`
     *
     * @param {Function} dateFilter
     * @param {Object} $translate
     * @returns {Function}
     */
    function formatAngularLongDate(dateFilter, $translate) {

        return function (input) {

            if (!input) {
                return null;
            }

            const date = new Date(input);

            const day = date.getDate();
            const monthAbbr = dateNames.shortMonthNames[date.getMonth()];
            const year = date.getFullYear();

            const formattedDate = $translate.instant('{{month}} {{day}}, {{year}}', { month: monthAbbr, day, year });
            const formattedTime = dateFilter(date, 'h:mm:ss a');

            return $translate.instant('{{date}}, {{time}}', {
                date: formattedDate,
                time: formattedTime
            });
        };
    }

    function timeAgo($translate) {

        return function (time, short) {

            if (!time) {
                return null;
            }

            if (angular.isDate(time)) {
                time = time.getTime();
            }
            else if (angular.isString(time)) {
                time = new Date(time).getTime();
            }

            const offset = Math.abs((Date.now() - time) / 1000);
            let span = [];
            const MINUTE = 60;
            const HOUR = 3600;
            const DAY = 86400;
            const WEEK = 604800;
            const MONTH = 2629744;
            const YEAR = 31556926;

            if (offset <= MINUTE) {
                return $translate.instant('now');
            }
            else if (offset < (MINUTE * 60)) {
                const count = Math.round(Math.abs(offset / MINUTE));
                const unit = short ? $translate.instant('min') : (count === 1 ? $translate.instant('minute') : $translate.instant('minutes'));
                span = [count, unit];
            }
            else if (offset < (HOUR * 24)) {
                const count = Math.round(Math.abs(offset / HOUR));
                const unit = short ? $translate.instant('hr') : (count === 1 ? $translate.instant('hour') : $translate.instant('hours'));
                span = [count, unit];
            }
            else if (offset < (DAY * 7)) {
                const count = Math.round(Math.abs(offset / DAY));
                const unit = count === 1 ? $translate.instant('day') : $translate.instant('days');
                span = [count, unit];
            }
            else if (offset < (WEEK * 4)) {
                const count = Math.round(Math.abs(offset / WEEK));
                const unit = short ? $translate.instant('wk') : (count === 1 ? $translate.instant('week') : $translate.instant('weeks'));
                span = [count, unit];
            }
            else if (offset < (MONTH * 12)) {
                const count = Math.round(Math.abs(offset / MONTH));
                const unit = short ? $translate.instant('mnth') : (count === 1 ? $translate.instant('month') : $translate.instant('months'));
                span = [count, unit];
            }
            else {
                const count = Math.round(Math.abs(offset / YEAR));
                const unit = short ? $translate.instant('yr') : (count === 1 ? $translate.instant('year') : $translate.instant('years'));
                span = [count, unit];
            }

            span = span.join(' ');

            return $translate.instant('{{span}} ago', { span });
        };
    }

    /**
     * Handles both future and past dates
     *
     * @param {Object} $translate
     * @returns {Function}
     */
    function timeFromNow($translate) {

        return function (time, short) {

            if (!time) {
                return null;
            }

            if (angular.isDate(time)) {
                time = time.getTime();
            }
            else if (angular.isString(time)) {
                time = new Date(time).getTime();
            }

            let isPast = false;
            let offset = (time - Date.now()) / 1000;
            if (offset < 0) {
                offset = Math.abs(offset);
                isPast = true;
            }

            let span = [];
            const MINUTE = 60;
            const HOUR = 3600;
            const DAY = 86400;
            const WEEK = 604800;
            const MONTH = 2629744;
            const YEAR = 31556926;

            if (offset <= MINUTE) {
                const text = $translate.instant('less than a minute');
                return isPast ? $translate.instant('{{text}} ago', { text }) : $translate.instant('{{text}} from now', { text });
            }
            else if (offset < (MINUTE * 60)) {
                const count = Math.round(Math.abs(offset / MINUTE));
                const unit = $translate.instant('min');
                span = [count, unit];
            }
            else if (offset < (HOUR * 24)) {
                const count = Math.round(Math.abs(offset / HOUR));
                const unit = $translate.instant('hr');
                span = [count, unit];
            }
            else if (offset < (DAY * 7)) {
                const count = Math.round(Math.abs(offset / DAY));
                const unit = count === 1 ? $translate.instant('day') : $translate.instant('days');
                span = [count, unit];
            }
            else if (offset < (WEEK * 4)) {
                const count = Math.round(Math.abs(offset / WEEK));
                const unit = short ? $translate.instant('wk') : (count === 1 ? $translate.instant('week') : $translate.instant('weeks'));
                span = [count, unit];
            }
            else if (offset < (MONTH * 12)) {
                const count = Math.round(Math.abs(offset / MONTH));
                const unit = short ? $translate.instant('mnth') : (count === 1 ? $translate.instant('month') : $translate.instant('months'));
                span = [count, unit];
            }
            else {
                const count = Math.round(Math.abs(offset / YEAR));
                const unit = short ? $translate.instant('yr') : (count === 1 ? $translate.instant('year') : $translate.instant('years'));
                span = [count, unit];
            }

            span = span.join(' ');

            return isPast ? $translate.instant('{{span}} ago', { span }) : $translate.instant('{{span}} from now', { span });
        };
    }

    function formatToTheSecondRoundedUp(dateFilter, $translate) {

        return function (input) {

            if (!input && input !== 0) {
                return null;
            }

            if (input < 1000) {
                return $translate.instant('less than a second');
            }

            return dateFilter(new Date(input), 'mm:ss');
        };
    }

    /**
     * Returns something like 11:04 or 01:05 by default
     * Can be enhanced to do am/pm based on the user in the future
     *
     * @param {Function} dateFilter
     * @param {Object} model
     * @returns {Function}
     */
    function formatTime(dateFilter,  model) {

        return function (input) {

            return dateFilter(new Date(input), pickTimeFormat(model));
        };
    }

    /**
     * Function that gives back the right time format
     *
     * @param {Object} model
     * @returns {'h:mm a' | 'HH:mm'}
     */
    function pickTimeFormat(model) {

        if (model.me && model.me.am_pm_notation) {
            return 'h:mm a';
        }

        return 'HH:mm';
    }

    function secondsToDays($translate) {

        return function (input) {
            const days = Math.floor(moment.duration(input, 'seconds').asDays());

            if (days === 0) {
                return $translate.instant('less than a day');
            }

            const dayText = days === 1 ? $translate.instant('day') : $translate.instant('days');
            return days + ' ' + dayText;
        };
    }

    function secondsToDaysAndHours($translate) {

        return function (input) {

            if (input < 3600) {
                return $translate.instant('Less than an hour');
            }

            const formattedParts = [];
            const days = Math.floor(input / ( 3600 * 24 ));
            const hours = Math.floor(input % ( 3600 * 24 ) / 3600);

            if (days > 0) {
                const dayText = days === 1 ? $translate.instant('day') : $translate.instant('days');
                formattedParts.push(days + ' ' + dayText);
            }

            if (hours > 0) {
                const hourText = hours === 1 ? $translate.instant('hour') : $translate.instant('hours');
                formattedParts.push(hours + ' ' + hourText);
            }

            const andText = $translate.instant('and');
            return formattedParts.join(' ' + andText + ' ');
        };
    }
})();
